<template>
  <div class="px-4 py-5 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-6xl space-y-4">
      <div>
        <p class="text-xs font-semibold uppercase text-bochas-rojo">
          La Federación
        </p>
        <h2 class="mt-1 text-xl font-bold text-white sm:text-2xl">
          Autoridades y documentos
        </h2>
      </div>

      <div v-if="loading" class="card-fbochas p-6 text-bochas-text-muted">
        Cargando información...
      </div>

      <template v-else>
        <section class="card-fbochas p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3 class="text-base font-bold text-white">Autoridades</h3>
            <button class="admin-secondary-button" type="button" @click="addAuthority">
              Agregar autoridad
            </button>
          </div>

          <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div
              v-for="(item, index) in authorities"
              :key="index"
              class="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-3"
            >
              <div class="aspect-square overflow-hidden rounded-lg bg-black/30">
                <img
                  v-if="item.photoUrl"
                  :src="item.photoUrl"
                  :alt="item.nombre || item.cargo"
                  class="h-full w-full object-cover"
                />
                <div
                  v-else
                  class="flex h-full items-center justify-center text-sm text-bochas-text-muted"
                >
                  Sin imagen
                </div>
              </div>

              <SelectField
                v-model="item.grupo"
                :options="authorityGroupOptions"
                placeholder="Seleccioná un grupo"
              />
              <input v-model="item.cargo" class="admin-input" placeholder="Cargo" />
              <input v-model="item.nombre" class="admin-input" placeholder="Nombre" />
              <input v-model="item.photoUrl" class="admin-input" placeholder="URL de imagen" />

              <input
                :ref="(el) => setAuthorityInputRef(index, el)"
                type="file"
                class="hidden"
                accept="image/*"
                @change="uploadAuthorityImage(index, $event)"
              />
              <button
                type="button"
                class="btn-bochas-rojo w-full px-4 py-2 text-sm"
                :disabled="uploadingAuthorityIndex === index"
                @click="authorityInputRefs[index]?.click()"
              >
                {{ uploadingAuthorityIndex === index ? "Subiendo..." : "Subir imagen" }}
              </button>
            </div>
          </div>
        </section>

        <section class="card-fbochas p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3 class="text-base font-bold text-white">
              Reglamentaciones y estatutos
            </h3>
            <button class="admin-secondary-button" type="button" @click="addDocument">
              Agregar documento
            </button>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <div
              v-for="(doc, index) in documents"
              :key="index"
              class="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-3"
            >
              <input v-model="doc.titulo" class="admin-input" placeholder="Título" />
              <textarea
                v-model="doc.descripcion"
                rows="2"
                class="admin-input"
                placeholder="Descripción"
              ></textarea>
              <input v-model="doc.url" class="admin-input" placeholder="URL disponible después de subir" />
              <input
                :ref="(el) => setDocumentInputRef(index, el)"
                type="file"
                class="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                @change="uploadDocument(index, $event)"
              />
              <button
                type="button"
                class="btn-bochas-rojo w-full px-4 py-2 text-sm"
                :disabled="uploadingDocumentIndex === index"
                @click="documentInputRefs[index]?.click()"
              >
                {{ uploadingDocumentIndex === index ? "Subiendo..." : "Subir documento" }}
              </button>
            </div>
          </div>
        </section>

        <p v-if="error" class="text-sm text-bochas-rojo">{{ error }}</p>
        <p v-if="saved" class="text-sm text-emerald-300">Guardado.</p>

        <button
          type="button"
          class="btn-bochas-rojo px-5 py-2 text-sm"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? "Guardando..." : "Guardar cambios" }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import SelectField from "@/components/Common/SelectField.vue";
import {
  guardarFederationContent,
  obtenerFederationContent,
} from "@/services/federation/federationContentService";
import { subirDocumentoFederacion } from "@/services/files/filesService";

const authorities = ref([]);
const groups = ref([]);
const documents = ref([]);
const loading = ref(true);
const saving = ref(false);
const saved = ref(false);
const error = ref("");
const uploadingAuthorityIndex = ref(null);
const uploadingDocumentIndex = ref(null);
const authorityInputRefs = ref({});
const documentInputRefs = ref({});
const authorityGroupOptions = [
  { value: "Comisión Directiva", label: "Comisión Directiva" },
  { value: "Tribunal de Disciplina", label: "Tribunal de Disciplina" },
  { value: "Secretario Administrativo", label: "Secretario Administrativo" },
  { value: "Secretario de Prensa", label: "Secretario de Prensa" },
];

function setAuthorityInputRef(index, el) {
  if (el) authorityInputRefs.value[index] = el;
}

function setDocumentInputRef(index, el) {
  if (el) documentInputRefs.value[index] = el;
}

function addAuthority() {
  authorities.value.push({
    grupo: "Comisión Directiva",
    cargo: "",
    nombre: "",
    photoUrl: "",
  });
}

function addDocument() {
  documents.value.push({ titulo: "", descripcion: "", url: "" });
}

async function load() {
  loading.value = true;
  try {
    const data = await obtenerFederationContent();
    authorities.value = data.authorities.length
      ? data.authorities.map((item) => ({
          grupo: item.grupo || "Comisión Directiva",
          cargo: item.cargo || "",
          nombre: item.nombre || "",
          photoUrl: item.photoUrl || "",
        }))
      : [
          {
            grupo: "Comisión Directiva",
            cargo: "",
            nombre: "",
            photoUrl: "",
          },
        ];
    groups.value = (
      data.groups.length
        ? data.groups
        : [
            { titulo: "Miembros Honorarios", miembros: [] },
            { titulo: "Comisión Revisora de Cuentas", miembros: [] },
            { titulo: "Tribunal de Disciplina", miembros: [] },
          ]
    ).map((group) => ({
      ...group,
      miembrosText: (group.miembros || []).join("\n"),
    }));
    documents.value = data.documents.length
      ? data.documents
      : [{ titulo: "", descripcion: "", url: "" }];
  } finally {
    loading.value = false;
  }
}

async function uploadAuthorityImage(index, event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;

  uploadingAuthorityIndex.value = index;
  error.value = "";
  try {
    const res = await subirDocumentoFederacion(file, {
      title: authorities.value[index]?.nombre || file.name,
    });
    authorities.value[index].photoUrl = res.url;
  } catch (_) {
    error.value = "No se pudo subir la imagen.";
  } finally {
    uploadingAuthorityIndex.value = null;
  }
}

async function uploadDocument(index, event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;

  uploadingDocumentIndex.value = index;
  error.value = "";
  try {
    const res = await subirDocumentoFederacion(file, {
      title: documents.value[index]?.titulo || file.name,
    });
    documents.value[index].url = res.url;
  } catch (_) {
    error.value = "No se pudo subir el documento.";
  } finally {
    uploadingDocumentIndex.value = null;
  }
}

async function save() {
  saving.value = true;
  saved.value = false;
  error.value = "";
  try {
    await guardarFederationContent({
      authorities: authorities.value,
      groups: groups.value.map((group) => ({
        titulo: group.titulo,
        miembros: String(group.miembrosText || "")
          .split("\n")
          .map((name) => name.trim())
          .filter(Boolean),
      })),
      documents: documents.value,
    });
    saved.value = true;
  } catch (_) {
    error.value = "No se pudo guardar la información.";
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.admin-input {
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.6rem 0.75rem;
  font-size: 0.875rem;
  color: white;
  outline: none;
}

.admin-input:focus {
  border-color: #a52121;
}

.admin-secondary-button {
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.45rem 0.8rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: white;
  transition: background-color 180ms ease;
}

.admin-secondary-button:hover {
  background: rgba(255, 255, 255, 0.1);
}
</style>
